import { useEffect, useRef, useState } from "react";

import styles from "./UserMenu.module.css";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { getInitials } from "@/lib/initials";
import { authService } from "@/services/authService";

/**
 * Account chip + dropdown. Shows the signed-in user's photo (falling back to
 * initials) and a CSRF-protected sign-out. Sign-out is a real form POST so the
 * browser follows the server's cross-origin Microsoft logout redirect. The
 * whole menu hides when there is no signed-in user (e.g. unauthenticated dev).
 */
export function UserMenu() {
  const { user, csrfToken } = useCurrentUser();
  const [open, setOpen] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!user) return null;

  const label = user.name || user.username || "";
  const accountLabel = label ? `${label} account menu` : "Open account menu";
  const showPhoto = Boolean(user.photoUrl) && !photoFailed;

  return (
    <div className={open ? `${styles.menu} ${styles.open}` : styles.menu} ref={menuRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-label={accountLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        title={label ? `${label} account menu` : "Account"}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.avatarShell} aria-hidden="true">
          {showPhoto && (
            <img
              className={styles.portrait}
              src={user.photoUrl ?? undefined}
              alt=""
              onError={() => setPhotoFailed(true)}
            />
          )}
          {!showPhoto && <span className={styles.avatar}>{getInitials(label)}</span>}
        </span>
      </button>

      {open && (
        <div className={styles.panel} role="menu">
          <div className={styles.identity}>
            <span className={styles.identityName}>{user.name ?? user.username}</span>
            {user.name && user.username && (
              <span className={styles.identityEmail}>{user.username}</span>
            )}
            {user.roles.length > 0 && (
              <span className={styles.identityRole}>
                {user.roles.map((r) => r.charAt(0).toUpperCase() + r.slice(1)).join(" · ")}
              </span>
            )}
          </div>
          <form className={styles.form} action={authService.signOutPath} method="post">
            <input type="hidden" name="csrfToken" value={csrfToken ?? ""} />
            <button className={styles.item} type="submit" role="menuitem">
              Sign out
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
